// Copied from coulomb-commons-ui soh-band-migration 0.1.0 (src/components/chartLabels.js),
// trimmed to the labels BucketSankeyChart draws (count labels, column axis, legend,
// caption). All positions are % of the CANVAS box.

import { xToPercent, yToPercent } from "./sankeyGeometry";
import { TYPE, DOT, LEGEND_ROW, CAPTION_ROW, CANVAS, HOST_BOX } from "./sankeyTokens";

// ---- Count labels (beside each bar) -------------------------------------------
//
// Each label reads "<count> (<share>%)", the share being the node's count over its
// column total (every bucket that month, SOH Unavailable included: the whole
// in-scope fleet). When that does not fit between two columns (12 months on a
// narrow card) every label switches to a stacked form: count, then share below.

export const COUNT_FONT_PX = 11;     // count, bold (TYPE.countLabel)
export const SHARE_FONT_PX = 10;     // share, regular (TYPE.countShare)
export const COUNT_LABEL_PAD_PX = 2; // pill padding, left and right
export const COUNT_LABEL_EDGE_PX = 2; // clearance to the next column's bar / box edge
export const COUNT_LABEL_INLINE_H_PX = 16;
export const COUNT_LABEL_STACKED_H_PX = 28;
/** Gap between the bar's right edge and its label, canvas units. */
export const COUNT_LABEL_GAP = 5;
/** Card width the canvas-unit sizes are tuned for when the real width is unknown. */
export const TYPICAL_BOX_WIDTH_PX = 1100;

/** Canvas units per screen px for a box `boxWidthPx` wide (falls back to a typical card). */
export const canvasUnitsPerPx = (boxWidthPx) => CANVAS.W / (boxWidthPx > 0 ? boxWidthPx : TYPICAL_BOX_WIDTH_PX);

/**
 * Share of the column total as display text: one decimal ("12.4%"), "<0.1%" for a
 * non-zero share under 0.05%, "" when there is no total.
 */
export function formatFleetShare(count, total) {
  const c = Number(count);
  const t = Number(total);
  if (!(t > 0) || !(c >= 0)) return "";
  const pct = (c / t) * 100;
  if (pct > 0 && pct < 0.05) return "<0.1%";
  return `${pct.toFixed(1)}%`;
}

// Approximate glyph widths (em) of the UI sans fonts, for when real text measurement
// is unavailable (tests). Bold adds ~6%.
const GLYPH_EM = { "%": 0.89, "(": 0.33, ")": 0.33, ".": 0.28, " ": 0.28, "<": 0.58, ",": 0.28 };
/** Estimated text width in px. */
export function estimateTextWidthPx(text, fontPx, bold = false) {
  const em = Array.from(String(text ?? "")).reduce((sum, ch) => sum + (GLYPH_EM[ch] ?? 0.56), 0);
  return em * fontPx * (bold ? 1.06 : 1);
}

/**
 * Real text measurement through a 2D canvas, or null where there is none (jsdom).
 * @returns {((text: string, fontPx: number, bold?: boolean) => number) | null}
 */
export function createCanvasTextMeasurer(fontFamily = "sans-serif") {
  if (typeof document === "undefined") return null;
  if (typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent || "")) return null;
  const ctx = document.createElement("canvas").getContext?.("2d");
  if (!ctx) return null;
  return (text, fontPx, bold = false) => {
    ctx.font = `${bold ? 700 : 400} ${fontPx}px ${fontFamily}`;
    return ctx.measureText(String(text ?? "")).width;
  };
}

/**
 * Positions one count label per node, BESIDE its bar: left edge COUNT_LABEL_GAP to the
 * right of the rect, vertically centred on it (canvas units, label centre). Every node
 * with a count gets a label.
 *
 * Mode: "inline" when every label fits the room to its right (next column's bar, or
 * the box edge for the last column) at this box width, else "stacked".
 * Within a column, labels closer than the label height are nudged apart (down first,
 * then back up from the bottom bound) so they never overlap, and stay between the
 * box top and the month axis.
 *
 * @param {object} graph       buildSankeyGraph result (absY, nodeMeta, distributions)
 * @param {{x0:number,x1:number}[]} columnX  computeColumnX result (viewBox units)
 * @param {{ boxWidthPx?: number, verticalUnitsPerPx?: number, measureText?: Function }} [options]
 *        boxWidthPx: rendered box width (horizontal units per px). verticalUnitsPerPx:
 *        canvas units per px vertically; differs from horizontal when the box is
 *        widened for panning (default: same as horizontal).
 * @returns {{ labels: object[], mode: "inline"|"stacked", labelHeight: number }}
 */
export function layoutCountLabels(
  graph,
  columnX,
  { boxWidthPx = 0, verticalUnitsPerPx, measureText = estimateTextWidthPx } = {}
) {
  const absY = graph?.absY;
  if (!absY || !graph?.nodeMeta || !Array.isArray(columnX)) return { labels: [], mode: "inline", labelHeight: 0 };
  const u = canvasUnitsPerPx(boxWidthPx);
  const measure = typeof measureText === "function" ? measureText : estimateTextWidthPx;
  const totals = (graph.distributions || []).map((d) => d.reduce((a, b) => a + b, 0));
  const lastIndex = columnX.length - 1;

  // Room to the right of a column's labels, px.
  const pitchRoomPx = columnX.length > 1
    ? (columnX[1].x0 - columnX[0].x1 - COUNT_LABEL_GAP) / u - COUNT_LABEL_EDGE_PX
    : Infinity;
  const rightRoomPx = lastIndex >= 0
    ? (CANVAS.W - (HOST_BOX.left + columnX[lastIndex].x1 + COUNT_LABEL_GAP)) / u - COUNT_LABEL_EDGE_PX
    : Infinity;

  const labels = [];
  graph.nodeMeta.forEach((meta) => {
    const slot = absY[meta.columnIndex]?.[meta.bucketIndex];
    const col = columnX[meta.columnIndex];
    if (!slot || !col) return;
    const countText = String(meta.count);
    const share = formatFleetShare(meta.count, totals[meta.columnIndex]);
    const countW = measure(countText, COUNT_FONT_PX, true);
    const inlineShare = share ? ` (${share})` : "";
    labels.push({
      key: `${meta.columnKey}-${meta.bucketIndex}`,
      nodeId: meta.id,
      columnIndex: meta.columnIndex,
      bucketIndex: meta.bucketIndex,
      count: meta.count,
      countText,
      shareText: share,
      text: `${countText}${inlineShare}`,
      inlineWidthPx: countW + measure(inlineShare, SHARE_FONT_PX) + 2 * COUNT_LABEL_PAD_PX,
      stackedWidthPx: Math.max(countW, measure(share, SHARE_FONT_PX)) + 2 * COUNT_LABEL_PAD_PX,
      x: HOST_BOX.left + col.x1 + COUNT_LABEL_GAP,
      y: HOST_BOX.top + (slot.y0 + slot.y1) / 2,
    });
  });

  const roomFor = (l) => (l.columnIndex === lastIndex ? rightRoomPx : pitchRoomPx);
  const mode = labels.every((l) => l.inlineWidthPx <= roomFor(l)) ? "inline" : "stacked";
  const uY = verticalUnitsPerPx > 0 ? verticalUnitsPerPx : u;
  const h = (mode === "inline" ? COUNT_LABEL_INLINE_H_PX : COUNT_LABEL_STACKED_H_PX) * uY;
  const minY = h / 2;
  const maxY = CANVAS.monthLabelY - h / 2;

  const byColumn = new Map();
  labels.forEach((l) => {
    l.widthPx = mode === "inline" ? l.inlineWidthPx : l.stackedWidthPx;
    if (!byColumn.has(l.columnIndex)) byColumn.set(l.columnIndex, []);
    byColumn.get(l.columnIndex).push(l);
  });
  byColumn.forEach((col) => {
    col.sort((a, b) => a.bucketIndex - b.bucketIndex);
    for (let i = 0; i < col.length; i++) {
      const floor = i === 0 ? minY : col[i - 1].y + h;
      col[i].y = Math.max(col[i].y, floor);
    }
    for (let i = col.length - 1; i >= 0; i--) {
      const ceiling = i === col.length - 1 ? maxY : col[i + 1].y - h;
      col[i].y = Math.min(col[i].y, ceiling);
    }
  });

  return { labels, mode, labelHeight: h };
}

/**
 * Width (px) of the widest INLINE count label, "<count> (<share>%)", including padding.
 * Independent of the box size, so the chart can size itself from it.
 */
export function maxInlineLabelWidthPx(graph, measureText = estimateTextWidthPx) {
  if (!graph?.nodeMeta) return 0;
  const measure = typeof measureText === "function" ? measureText : estimateTextWidthPx;
  const totals = (graph.distributions || []).map((d) => d.reduce((a, b) => a + b, 0));
  let max = 0;
  graph.nodeMeta.forEach((meta) => {
    const share = formatFleetShare(meta.count, totals[meta.columnIndex]);
    const width = measure(String(meta.count), COUNT_FONT_PX, true)
      + measure(share ? ` (${share})` : "", SHARE_FONT_PX)
      + 2 * COUNT_LABEL_PAD_PX;
    max = Math.max(max, width);
  });
  return max;
}

/** Style for a count label from layoutCountLabels (canvas units -> % of the box). */
export function countLabelStyle(label, mode = "inline") {
  return {
    position: "absolute",
    top: `${yToPercent(label.y)}%`,
    left: `${xToPercent(label.x)}%`,
    transform: "translateY(-50%)",
    whiteSpace: "nowrap",
    pointerEvents: "none",
    display: "flex",
    flexDirection: mode === "stacked" ? "column" : "row",
    alignItems: mode === "stacked" ? "flex-start" : "baseline",
    gap: mode === "stacked" ? 0 : "3px",
    lineHeight: mode === "stacked" ? "12px" : `${COUNT_LABEL_INLINE_H_PX - 4}px`,
    padding: `2px ${COUNT_LABEL_PAD_PX}px`,
    borderRadius: "4px",
    // Labels sit over the ribbons leaving the bar; a light pill keeps them readable.
    background: "rgba(255, 255, 255, 0.85)",
  };
}

/** Column labels along the bottom of the chart, centred on each column. */
export function buildAxisLabels(columns, colX) {
  return (columns || []).map((col, i) => ({
    key: col.key,
    text: col.label,
    style: {
      position: "absolute",
      top: `${yToPercent(CANVAS.monthLabelY)}%`,
      lineHeight: 1.2,
      left: `${xToPercent(colX[i] ?? 0)}%`,
      transform: "translateX(-50%)",
      whiteSpace: "nowrap",
      pointerEvents: "none",
      ...TYPE.monthLabel,
    },
  }));
}

/** Bucket colour legend; every bucket renders the same way. */
export function buildLegend(buckets) {
  const items = (buckets || []).map((b) => ({
    key: b.id,
    label: b.label,
    dotStyle: { ...DOT.legend, background: b.color, display: "inline-block" },
    textStyle: TYPE.legendItem,
    itemStyle: { display: "flex", alignItems: "center", gap: "6px" },
  }));
  return { rowStyle: LEGEND_ROW, items };
}

/** Caption line below the legend. */
export function buildCaption({
  columnNoun = "column",
  nounSingular = "item",
  nounPlural = "items",
  clickable = true,
  flowsClickable = false,
} = {}) {
  const parts = [
    `Each column is a ${columnNoun}`,
    `ribbon width is proportional to ${nounSingular} count`,
    "a ribbon's colour is the bucket it started in",
  ];
  if (clickable && flowsClickable) parts.push(`click any bucket or ribbon to see the ${nounPlural} in it`);
  else if (clickable) parts.push(`click any bucket to see the ${nounPlural} in it`);
  else if (flowsClickable) parts.push(`click any ribbon to see the ${nounPlural} that made that move`);
  return { text: `${parts.join(" · ")}.`, style: CAPTION_ROW };
}
